import React, { ChangeEvent, FunctionComponent } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { SlideIn, SubmitButton } from 'components';
import { FormGroup, Textarea } from 'components/chroma';

import { YesNoAnswer, YES_NO_OPTIONS } from 'scenes/common/types';
import { SegmentedControlQuestion } from 'scenes/common/components/Questions';

import { MotorFullPageProps } from 'scenes/motor/types';
import {
  getHasSpecialFeatures,
  getSelectedSpecialFeatures,
} from 'scenes/motor/selectors';

const HAS_SPECIAL_FEATURES = 'hasSpecialFeatures';
const SPECIAL_FEATURES = 'specialFeatures';

const MAX_LENGTH = 400;

const SpecialFeaturesStep: FunctionComponent<MotorFullPageProps> = ({
  funnelState,
  onNext,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    trigger,
    watch,
    getValues,
  } = useForm({
    mode: 'onChange',
  });

  const hasFeatures = getHasSpecialFeatures(funnelState);

  const watchedHasSpecialFeatures = watch(HAS_SPECIAL_FEATURES, hasFeatures);

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <SegmentedControlQuestion
        errors={errors}
        control={control}
        stateKey={HAS_SPECIAL_FEATURES}
        options={YES_NO_OPTIONS}
        defaultValue={hasFeatures}
        changeHandler={(
          evt: ChangeEvent<HTMLInputElement>,
          onChange: Function
        ) => {
          onChange(evt);
          trigger();
        }}
        question="Does the car have any other special features not mentioned previously?"
        helpText="Answer yes if there is something about the car you want us to make a note of on your policy  e.g. towbar, tinted windows etc. No matter what you answer, your premium will not change."
      />

      {watchedHasSpecialFeatures === YesNoAnswer.Yes && (
        <SlideIn>
          <Controller
            control={control}
            name={SPECIAL_FEATURES}
            defaultValue={getSelectedSpecialFeatures(funnelState)}
            rules={{
              validate: {
                validText: (value) =>
                  getValues(HAS_SPECIAL_FEATURES) === YesNoAnswer.No ||
                  (value && value.length <= MAX_LENGTH),
              },
            }}
            render={({ value, onChange }) => (
              <FormGroup
                id={SPECIAL_FEATURES}
                label="Please describe these special features as they will be included on your policy documentation for future reference"
              >
                <Textarea
                  id={SPECIAL_FEATURES}
                  name={SPECIAL_FEATURES}
                  placeholder="Max 400 characters"
                  maxLength={MAX_LENGTH}
                  onChange={onChange}
                  value={value}
                  valid={Boolean(value) && !errors[SPECIAL_FEATURES]}
                  error={Boolean(value) && Boolean(errors[SPECIAL_FEATURES])}
                />
              </FormGroup>
            )}
          />
        </SlideIn>
      )}
      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
    </form>
  );
};

export default SpecialFeaturesStep;
